import { Field, Input } from '@chakra-ui/react';
import type React from 'react';

interface RealTimeCurrencyInputProps {
  label?: string;
  value: string; // Raw numeric string (digits only)
  onChange: (value: string) => void; // Called with raw numeric string
  isRequired?: boolean;
  placeholder?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  isDisabled?: boolean;
}

/**
 * Format a numeric string as currency with $ prefix and commas
 * Examples: "" -> "", "5" -> "$5", "500" -> "$500", "5000" -> "$5,000"
 */
const formatCurrencyInput = (value: string): string => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');

  // Return empty string if no digits
  if (digitsOnly === '') {
    return '';
  }

  // Parse as number and format with commas
  const num = parseInt(digitsOnly, 10);
  if (Number.isNaN(num)) {
    return '';
  }

  // Format with commas using Intl.NumberFormat
  const formatted = new Intl.NumberFormat('en-US').format(num);

  // Add $ prefix
  return `$${formatted}`;
};

/**
 * Parse a formatted currency string back to a numeric string
 * Examples: "" -> "", "$5" -> "5", "$500" -> "500", "$5,000" -> "5000"
 */
const parseCurrencyInput = (value: string): string => {
  // Remove all non-digit characters
  return value.replace(/\D/g, '');
};

/**
 * RealTimeCurrencyInput - Currency input with real-time formatting
 *
 * Formats the display value with $ prefix and commas as the user types.
 * Stores the raw numeric string (digits only) in the value prop.
 *
 * Example usage:
 * ```tsx
 * <RealTimeCurrencyInput
 *   label="Amount"
 *   value={rawAmount}
 *   onChange={setRawAmount}
 * />
 * ```
 */
export const RealTimeCurrencyInput: React.FC<RealTimeCurrencyInputProps> = ({
  label,
  value, // Raw numeric string
  onChange,
  isRequired = false,
  placeholder,
  helperText,
  size = 'md',
  isDisabled = false,
}) => {
  // Format the raw value for display
  const displayValue = formatCurrencyInput(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Parse to get raw numeric string
    const rawValue = parseCurrencyInput(inputValue);
    // Update parent with raw value
    onChange(rawValue);
  };

  const inputElement = (
    <Input
      type="text"
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      size={size}
      disabled={isDisabled}
      readOnly={isDisabled}
    />
  );

  // If no label, return just the input
  if (!label) {
    return inputElement;
  }

  return (
    <Field.Root required={isRequired}>
      <Field.Label>
        {label} {isRequired && <Field.RequiredIndicator />}
      </Field.Label>
      {inputElement}
      {helperText && <Field.HelperText>{helperText}</Field.HelperText>}
    </Field.Root>
  );
};
